import React, { useEffect, useState } from 'react';
/**
 * `CustomerPersonalDetail` captures personal details and address for the
 * customer journey.
 *
 * Behaviour:
 * - Coordinates validation and update cycles for `PersonalDetailPage` and
 *   `AddressManager`.
 * - Updates journey personal details and (when empty) copies address into
 *   single payment billing address.
 * - Navigates back to create plan or forward to summary.
 * - Adjusts postcode/address rules for DE/AT sites.
 */
import { useLocation, useNavigate } from 'react-router-dom';
import PersonalDetailPage from '../../common/PersonalDetail/PersonalDetailPage';
import AddressManager from '../../common/Address/AddressManager';
import { PageAbsolutePath, PageRelativePath } from '../../../consts/Common';
import { AddressDetails, PersonalDetailType } from '../../common/CommonTypes';
import PageHeader from '../../common/PageHeader';
import { ButtonContinue } from '../../common/Button/ButtonContinue';
import { ButtonBack } from '../../common/Button/ButtonBack';
import { useJourney } from '../Layout';
import { CommonValidation } from '../../common/CommonValidation/commonValidation';
import { useTranslation } from 'react-i18next';
import { LocationVehicleId } from './CustomerVehicleDetailsPage';
import { CountryIsoCodes } from '../../common/Constants';
import { Helmet } from 'react-helmet';
import { useAppSelector } from '../../../app-state/Hooks';

const CustomerPersonalDetail = (): JSX.Element => {
  const { journey, setPersonalDetails, setAddressSinglePayment, onPageChange } =
    useJourney();

  const { personalDetail, singlePaymentDetails, token } = journey;
  const { t } = useTranslation();
  const [validateAddress, setValidateAddress] = useState<boolean | null>(null);
  const [updateAddress, setUpdateAddress] = useState<boolean | null>(null);
  const [validatePersonalDetail, setValidatePersonalDetail] = useState<
    boolean | null
  >(null);
  const [updatePersonalDetail, setUpdatePersonalDetail] = useState<
    boolean | null
  >(null);
  const [goBack, setGoBack] = useState(false);
  const [goForward, setGoForward] = useState(false);
  const navigate = useNavigate();
  const userToken = useAppSelector((s) => s.user.token);
  const appConfig = useAppSelector((s) => s.appConfig);
  const landingCms = useAppSelector((s) => s.content.landingPage);
  const { useVinInput } = landingCms;
  const cms = useAppSelector((s) => s.content.customerPage);
  const { state } = useLocation();
  const germanWebsite =
    appConfig.countryCode === CountryIsoCodes.DE ||
    appConfig.countryCode === CountryIsoCodes.AT;

  useEffect(() => {
    if (updateAddress === false && goForward === true) {
      updateBillingAddress(personalDetail.addressDetails);
      onPageChange(PageAbsolutePath.Customer.PersonalDetails, isPageValid());
      navigate(PageRelativePath.Summary, {
        state: {
          vehicleIdType: (state as LocationVehicleId)?.vehicleIdType
        }
      });
    }

    if (updateAddress === false && goBack === true) {
      onPageChange(PageAbsolutePath.Customer.PersonalDetails, isPageValid());
      navigate(PageRelativePath.CreatePlan, {
        state: {
          vehicleIdType: (state as LocationVehicleId)?.vehicleIdType
        }
      });
    }
  }, [updateAddress]);

  useEffect(() => {
    if (updatePersonalDetail === false) {
      setUpdateAddress(true);
    }
  }, [updatePersonalDetail]);

  useEffect(() => {
    if (isPageValid()) {
      setUpdatePersonalDetail(true);
    }
  }, [validatePersonalDetail, validateAddress]);

  const onPersonalDetailValidated = (isPDValid: boolean) => {
    if (isPDValid === true) {
      setValidatePersonalDetail(false);
    } else {
      setValidatePersonalDetail(null);
      setGoForward(false);
    }
  };

  const onAddressValidated = (isAddressValid: boolean) => {
    if (isAddressValid === true) {
      setValidateAddress(false);
    } else {
      setValidateAddress(null);
      setGoForward(false);
    }
  };

  const onAddressUpdate = (address: AddressDetails) => {
    const newPersonalDetail = {
      ...personalDetail,
      addressDetails: address
    };

    setPersonalDetails(newPersonalDetail);
    setUpdateAddress(false);
  };

  const onUpdatePersonalDetail = (pd: PersonalDetailType) => {
    setPersonalDetails(pd);
    setUpdatePersonalDetail(false);
  };

  const onBack = () => {
    setUpdatePersonalDetail(true);
    setGoBack(true);
  };

  const onCheckout = () => {
    setValidateAddress(true);
    setValidatePersonalDetail(true);
    setGoForward(true);
  };

  const isPageValid = (): boolean => {
    const isValid =
      validatePersonalDetail === false && validateAddress === false;

    return isValid;
  };

  const updateBillingAddress = (address: AddressDetails) => {
    if (addressIsEmpty(singlePaymentDetails.billingAddress)) {
      setAddressSinglePayment(address);
    }
  };

  const addressIsEmpty = (address: AddressDetails): boolean => {
    const isAddressEmpty =
      CommonValidation.isEmpty(address.addressLine1) &&
      CommonValidation.isEmpty(address.addressLine2) &&
      CommonValidation.isEmpty(address.townOrCity) &&
      CommonValidation.isEmpty(address.country) &&
      CommonValidation.isEmpty(address.region) &&
      CommonValidation.isEmpty(address.postCode);
    return isAddressEmpty;
  };
  const emailTooltipHeader = t('personal-details.email-tooltip-header');

  const phoneNumberTooltipHeader = t(
    'personal-details.phone-number-tooltip-header'
  );

  const dobTooltipHeader = undefined;

  return (
    <div>
      <Helmet>
        <title>{t('page-title.personal-details')}</title>
      </Helmet>
      <PageHeader
        title={cms.title}
        description={cms.description}
        information={cms.information}
        warning={cms.warning}
        descriptionClass='p2'
      />
      <PersonalDetailPage
        initialPersonalDetail={personalDetail}
        validate={validatePersonalDetail || false}
        update={updatePersonalDetail || false}
        token={useVinInput ? userToken : token}
        onValidated={onPersonalDetailValidated}
        onUpdate={onUpdatePersonalDetail}
        showEmailConfirmation={true}
        dobTooltipHeader={dobTooltipHeader}
        emailTooltipHeader={emailTooltipHeader}
        phoneNumberTooltipHeader={phoneNumberTooltipHeader}
        isDoBOptional={cms.isDateOfBirthOptional}
      />

      <div className='control'>
        <AddressManager
          findAddressTitle={t('personal-details.address-title-text')}
          initialAddress={personalDetail?.addressDetails}
          update={updateAddress || false}
          validate={validateAddress || false}
          token={token}
          onAddressUpdate={onAddressUpdate}
          onAddressValidated={onAddressValidated}
          allowManualEntry
          maxPostCodeLimit={germanWebsite ? 6 : 10}
          allowAlphabetInPostCode={!germanWebsite}
          isMandatoryAddressline2={germanWebsite}
        />
      </div>
      <div className='flex-row form-nav mobile-width-90pc'>
        <ButtonBack
          name='btnBack'
          onClick={onBack}
          text={t('customer-button.back-buttont-text')}
        />
        <ButtonContinue
          name='btn-checkout'
          text={t('customer-button.button-checkout-text')}
          onClick={onCheckout}
        />
      </div>
    </div>
  );
};

export default CustomerPersonalDetail;
